import { useMemo } from "react";
import { Button, Icon, Modal, Typography } from "@/components/atoms";
import { GetSubscriptionPlansResponse, SubscriptionPlan } from "@/redux/apis/subscription/types";
import { extractPlanFeatures } from "../helpers";

interface UpgradeSuccessModalProps {
  open: boolean;
  onClose: () => void;
  operatorName?: string;
  targetPlan?: SubscriptionPlan;
  subscriptionData?: GetSubscriptionPlansResponse;
}

export const UpgradeSuccessModal = ({
  open,
  onClose,
  operatorName = "Operator",
  targetPlan,
  subscriptionData,
}: UpgradeSuccessModalProps) => {
  const planName = targetPlan?.subscriptionName || "New Plan";
  const iconSvg = targetPlan?.iconSvg;

  // Extract features from subscription data, or fall back to featuredFeatures
  const features = useMemo(() => {
    if (subscriptionData && targetPlan?.subscriptionId) {
      const extractedFeatures = extractPlanFeatures(subscriptionData, targetPlan.subscriptionId);
      if (extractedFeatures.length > 0) {
        return extractedFeatures;
      }
    }
    return targetPlan?.featuredFeatures || [];
  }, [subscriptionData, targetPlan]);

  const handleBackToSubscriptions = () => {
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} className="w-[85vw] md:w-[458px] lg:w-[458px]">
      <div className="flex flex-col items-center gap-6">
        {/* Thumbs Up Icon */}
        <div className="flex h-[80px] w-[80px] items-center justify-center rounded-full bg-primary-light">
          <Icon size="xl" name="Like1" variant="Bold" className="text-primary" />
        </div>

        {/* Title and Description */}
        <div className="flex flex-col gap-4 text-center text-neutral-dark-gray">
          <Typography variant="h2">You&apos;ve upgraded your plan! 🚀</Typography>
          <Typography variant="paragraph">
            Thanks for upgrading. Your new plan is now active and you&apos;ve unlocked additional features for your {operatorName} account.
          </Typography>
        </div>

        {/* Plan Header and Features */}
        <div className="w-full">
          <div className="flex flex-row items-center gap-3">
            {iconSvg && (
              <div className="flex h-10 w-10 flex-shrink-0 [&>svg]:h-full [&>svg]:w-full" dangerouslySetInnerHTML={{ __html: iconSvg }} />
            )}
            <Typography variant="title">{planName}</Typography>
          </div>

          {/* Features List */}
          {features.length > 0 && (
            <div className="relative mt-4">
              <ul className="flex max-h-[200px] flex-col gap-1 overflow-y-auto rounded-lg border border-neutral-mid-gray bg-neutral-surface-gray p-4 pl-8 shadow-inner">
                {features.map((feature, index) => (
                  <li key={index} className="list-disc text-neutral-dark-gray">
                    <Typography variant="paragraph">{feature}</Typography>
                  </li>
                ))}
              </ul>
              {/* Fade gradient at bottom to indicate scrollable content */}
              <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-8 rounded-b-lg bg-gradient-to-t from-neutral-surface-gray to-transparent" />
            </div>
          )}
        </div>

        {/* Action Button */}
        <Button type="button" size="lg" variant="primary" className="w-full" onClick={handleBackToSubscriptions}>
          Back to Subscriptions
        </Button>
      </div>
    </Modal>
  );
};
